import { TextField as MUITextField } from '@mui/material';
import { Box } from '@src/components/Box';
import { Icon } from '@src/components/Icon';
import { Label } from '@src/components/Typography';
import { palette } from '@src/theme';
import identity from 'lodash/identity';
import React from 'react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { BASE_TEXT_FIELD_HEIGHT } from '../TextField/util';
import { useSSNField } from './hooks';
import {
  DEFAULT_HELPER_TEXT,
  DEFAULT_PLACEHOLDER,
  LAST_4_ONLY_PLACEHOLDER,
  SSNFieldControllerProps,
  SSNFieldProps,
} from './interface';

const SSNFieldInput = (props: SSNFieldProps & SSNFieldControllerProps) => {
  const {
    disabled,
    error,
    formatOnChange = identity,
    label,
    labelStyle = 'stacked',
    name,
    onChange,
    parseValue = identity,
    placeholder,
    value,
    helperText = DEFAULT_HELPER_TEXT,
    last4only = false,
  } = props;

  const getHelperText = (error: FieldError | undefined) => {
    const parsedError = error || undefined;
    return parsedError ? parsedError.message : helperText;
  };

  const onChangeWithFormat = (val: any) => {
    const formattedValue = formatOnChange(val, value);
    onChange(formattedValue);
  };
  const { changeHidden, handleChange, inputRef, hideValue, maskValue } = useSSNField(
    name,
    onChangeWithFormat,
    last4only,
    parseValue,
  );

  const defaultPlaceholder = last4only ? LAST_4_ONLY_PLACEHOLDER : DEFAULT_PLACEHOLDER;
  const placeholderText = placeholder?.length ? placeholder : defaultPlaceholder;

  return (
    <Box>
      {labelStyle === 'stacked' && <Label size="s">{label}</Label>}
      <MUITextField
        aria-label="social-secutity-number-field"
        disabled={disabled}
        error={!!error}
        fullWidth
        helperText={getHelperText(error)}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          label: labelStyle === 'inline' ? label : undefined,
          endAdornment: (
            <div onClick={changeHidden} style={{ cursor: 'pointer' }}>
              <Icon name="eye-regular" />
            </div>
          ),
          sx: {
            boxSizing: 'border-box',
            fontSize: '14px',
            height: `${BASE_TEXT_FIELD_HEIGHT}px`,
          },
        }}
        inputRef={inputRef}
        label={labelStyle === 'inline' ? label : undefined}
        name={name}
        onChange={handleChange}
        placeholder={placeholderText}
        defaultValue={hideValue(maskValue(value || ''))}
        sx={{
          '::placeholder': {
            color: palette.controls.outline,
            opacity: '1',
          },
        }}
      />
    </Box>
  );
};

export const SSNField = ({ name, ...rest }: SSNFieldProps) => {
  const { control } = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <SSNFieldInput error={error} name={name} onChange={onChange} value={value} {...rest} />
      )}
    />
  );
};
